import * as d3 from "d3";
import { getPlotDimensions } from "@/components/VivoPlots/functions/utils/getPlotDimensions";
import { bindTickTooltip } from "./utils/tickTooltip";
import type { configType } from "@/components/VivoPlots/types";
import { addFilterButton } from "./addFilterButton";

export type PlotSize = "small" | "medium" | "large";

export const DEFAULT_PLOT_SIZE: PlotSize = "large";

const FONT_SIZE_MAP = {
  title: {
    small: 14,
    medium: 16,
    large: 18,
  },
  subtitle: {
    small: 12,
    medium: 13,
    large: 14,
  },
} as const;

const RIGHT_PADDING = {
  twoLines: {
    withFilters: 140,
    withoutFilters: 120,
  },
  oneLine: {
    withFilters: {
      small: 117,
      default: 120,
    },
    withoutFilters: 100,
  },
};

const addPlotTitle = (
  titleLines: string[],
  plotClassName: string,
  xPosition: number,
  baselineY: number,
  fontSize: number,
) => {
  const lineHeight = fontSize;

  titleLines.forEach((line, index) => {
    d3.select(`svg.${plotClassName}`)
      .append("text")
      .classed("title labels", true)
      .attr("font-family", "Work Sans")
      .attr("x", xPosition)
      .attr("y", baselineY + index * lineHeight)
      .attr("text-anchor", "left")
      .attr("font-size", `${fontSize}px`)
      .attr("font-weight", 600)
      .attr("fill", "#2B4752")
      .text(line);
  });
};

export function splitTextByWidth(
  text: string,
  widthInPx: number,
  maxLines = 2,
) {
  // if (widthInPx <= 0) {
  //   throw new Error("Width should be greater than 0");
  // }
  if (!text) {
    return [];
  }

  const words = text.split(" ");
  let currentLine = "";
  const lines = [];

  for (const word of words) {
    const testLine = currentLine + (currentLine ? " " : "") + word;
    const testWidth = testLine.length * 9;

    if (testWidth > widthInPx) {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        // If a single word is wider than the allowed width, split it
        lines.push(word);
        currentLine = "";
      }
    } else {
      currentLine = testLine;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }
  if (lines.length > maxLines) {
    lines[maxLines - 1] = lines[maxLines - 1] + "...";
    return lines.slice(0, maxLines);
  }
  return lines;
}

const addPlotSubtitle = (
  subtitleLines: string[],
  plotClassName: string,
  xPosition: number,
  yPosition: number,
  fontSize: number,
) => {
  const lineHeight = fontSize;

  subtitleLines.forEach((line, index) => {
    d3.select(`svg.${plotClassName}`)
      .append("text")
      .classed("subtitle labels", true)
      .attr("font-family", "Work Sans")
      .attr("x", xPosition)
      .attr("y", yPosition + index * lineHeight)
      .attr("text-anchor", "left")
      .attr("font-size", `${fontSize}px`)
      .attr("fill", "#556C75")
      .text(line);
  });
};

const addLeftAxisName = (
  label: string,
  plotClassName: string,
  {
    height,
    marginTop,
    padding,
  }: { height: number; marginTop: number; marginLeft: number; padding: number },
) => {
  if (label) {
    const splitText = splitTextByWidth(label, height, 1);
    const labelElement = d3
      .select(`svg.${plotClassName}`)
      .append("text")
      .classed("left-axis-name labels", true);
    labelElement
      .attr("font-family", "Work Sans") // Add this line
      .attr("transform", "rotate(-90)")
      .attr("x", -(height / 2 + marginTop))
      .attr("y", padding + 10)

      .attr("text-anchor", "middle")
      .attr("font-size", "14px")
      .text(splitText[0]);

    labelElement.call((s) =>
      bindTickTooltip(
        s as d3.Selection<SVGTextElement, unknown, any, any>,
        plotClassName,
        { displayedText: splitText[0], fullText: label },
      ),
    );
  }
};

const addBottomAxisName = (
  label: string,
  plotClassName: string,
  {
    width,
    height,
    marginTop,
    marginLeft,
    marginBottom,
    padding,
  }: {
    width: number;
    height: number;
    marginTop: number;
    marginLeft: number;
    marginBottom: number;
    padding: number;
  },
) => {
  const y = height + marginTop + marginBottom - padding + 5;
  if (label) {
    const splitText = splitTextByWidth(label, width, 1);

    const labelElement = d3
      .select(`svg.${plotClassName}`)
      .append("text")
      .classed("bottom-axis-name labels", true)
      .attr("font-family", "Work Sans")
      .attr("x", width / 2 + marginLeft)
      .attr("y", y)
      .attr("text-anchor", "middle")
      .attr("font-size", "14px")
      .text(splitText[0]);

    labelElement.call((s) =>
      bindTickTooltip(
        s as d3.Selection<SVGTextElement, unknown, any, any>,
        plotClassName,
        { displayedText: splitText[0], fullText: label },
      ),
    );
  }
};

const addSeparatorLine = (
  plotClassName: string,
  x1: number,
  x2: number,
  yPosition: number,
) => {
  d3.select(`svg.${plotClassName}`)
    .append("line")
    .classed("separator labels", true)
    .attr("x1", x1)
    .attr("x2", x2)
    .attr("y1", yPosition)
    .attr("y2", yPosition)
    .attr("stroke", "#E0E0E0")
    .attr("stroke-width", 1);
};

export const addLabels = (
  {
    title,
    subtitle,
    x,
    y,
  }: { title: string; subtitle: string; x: string; y: string },
  plotClassName: string,
  plotConfig: configType,
) => {
  const dimensions = getPlotDimensions(plotClassName, plotConfig);
  const { width, marginLeft, marginRight, marginTop, padding } = dimensions;

  const hasFilters = plotConfig?.hasPlotFilters || false;
  const plotSize: PlotSize = plotConfig?.plotSize || DEFAULT_PLOT_SIZE;

  // First, determine the padding assuming one line
  let oneLineRightPadding;
  if (hasFilters) {
    oneLineRightPadding =
      plotSize === "small"
        ? RIGHT_PADDING.oneLine.withFilters.small
        : RIGHT_PADDING.oneLine.withFilters.default;
  } else {
    oneLineRightPadding = RIGHT_PADDING.oneLine.withoutFilters;
  }

  // Check if title wraps with the one-line padding
  const preliminaryWidth =
    width + marginLeft + marginRight - oneLineRightPadding;
  const preliminaryLines = splitTextByWidth(title, preliminaryWidth, 2);
  const willBeTwoLines = preliminaryLines.length === 2;

  // If it wraps to two lines, use the two-line padding instead
  const rightPadding = willBeTwoLines
    ? hasFilters
      ? RIGHT_PADDING.twoLines.withFilters
      : RIGHT_PADDING.twoLines.withoutFilters
    : oneLineRightPadding;

  const titleWidth = width + marginLeft + marginRight - rightPadding;
  const titleLines = splitTextByWidth(title, titleWidth, 2);
  const subtitleWidth = width + marginLeft + marginRight;
  const subtitleLines = splitTextByWidth(subtitle, subtitleWidth, 1);

  let titleFontSize = FONT_SIZE_MAP.title[plotSize];
  if (
    (plotSize === "small" || plotSize === "medium") &&
    titleLines.length === 1 &&
    !hasFilters
  ) {
    titleFontSize = 18;
  }
  const subtitleFontSize = FONT_SIZE_MAP.subtitle[plotSize];

  // For medium and large plots, move title up to better align with controls
  let titleBaselineY;
  if (titleLines.length === 1) {
    titleBaselineY =
      plotSize === "medium" || plotSize === "large"
        ? padding + 14
        : padding + 18;
  } else {
    titleBaselineY = padding + 14;
  }
  const titleXPosition = hasFilters ? padding + 34 : padding;

  const spaceBetweenTitleAndSubtitle = titleLines.length === 1 ? 22 : 16;

  const subtitleYPosition =
    titleBaselineY +
    titleFontSize * (titleLines.length - 1) +
    spaceBetweenTitleAndSubtitle;

  const subtitleXPosition = padding;

  const separatorY = marginTop - 12;
  const separatorX2 = marginLeft + width + marginRight + 2 * padding;

  if (hasFilters) {
    const filterClickHandler = plotConfig?.filterClickHandler;
    // For medium and large plots, reduce Y offset to align with controls
    const filterYOffset =
      plotSize === "medium" || plotSize === "large" ? -2 : 2;
    addFilterButton(plotClassName, padding, filterClickHandler, filterYOffset);
  }

  addPlotTitle(
    titleLines,
    plotClassName,
    titleXPosition,
    titleBaselineY,
    titleFontSize,
  );

  addPlotSubtitle(
    subtitleLines,
    plotClassName,
    subtitleXPosition,
    subtitleYPosition,
    subtitleFontSize,
  );

  addSeparatorLine(plotClassName, 0, separatorX2, separatorY);

  const renderedAxisLabels = getRenderedAxisLabels({ x, y }, plotConfig);

  addLeftAxisName(renderedAxisLabels.leftLabel, plotClassName, dimensions);
  addBottomAxisName(renderedAxisLabels.bottomLabel, plotClassName, dimensions);
};

export const getRenderedAxisLabels = (
  { x, y }: { x?: string; y?: string },
  plotConfig: configType,
) => {
  const isHorizontal = plotConfig?.orientation === "horizontal";
  const showLeftAxisLabel = plotConfig?.showYAxisLabel ?? true;
  const showBottomAxisLabel = plotConfig?.showXAxisLabel ?? true;

  return {
    leftLabel: showLeftAxisLabel ? (isHorizontal ? x : y) : "",
    bottomLabel: showBottomAxisLabel ? (isHorizontal ? y : x) : "",
  };
};
